import { defineStore } from 'pinia'
import { CampaignRegulatoryRequirement, getCampaignData } from './useCampaign'
import { OrderResponseObject, postOrder, postBasket } from '../api'
import { usePersonalAccountDataState } from '../account/useAccountDataPersonal'
import {
    deletePendingInvestmentCookie,
    getPendingInvestmentCookie,
    setPendingInvestmentCookie,
} from './usePendingInvestmentProcess'
import { isLegalProfile, isNaturalProfile } from './useUserProfileType'
import { useLegalAccountDataState } from '../account/useAccountDataLegal'
import { promiseHandler } from '../helpers'
import { CouponCode } from '../../typings'
import { useInvestmentProcessNavigation } from '../account/useInvestmentProcessNavigation'
import useUtmParams from '../useUtmParams'

const campaignData = getCampaignData()

export const useInvestmentProcessDataState = defineStore('investment-process-data', {
    state: () => {
        return {
            acceptAllWarnings: false,
            acceptedRisks: [],
            agioDisagioAmount: 0,
            altfgLegalEntity: false,
            altfgMaximumAssets: false,
            altfgMaximumNetIncome: false,
            altfgProfessionalInvestor: false,
            appliedCoupons: [] as CouponCode[],
            brokerDisclaimer: null as boolean | null,
            brokerNumber: null as string | null,
            contractType: null,
            createdOrder: null as OrderResponseObject | null,
            giftMembership: false,
            incentiveUid: null,
            interestPaymentType: null,
            investmentAmount: null,
            issuingAuthority: null as string | null,
            nyalaPassphrase: null as string | null,
            pieces: 0,
            savingsPlan: false,
            acceptPublicInvestmentConsent: false,
            step: 0,
            vibAddress: null as string | null,
            vibCity: null as string | null,
            vibCurrentLocation: null as string | null,
            vibDate: null as string | null,
            vibDateOfBirth: null as string | null,
            vibEmail: null as string | null,
            vibFirstName: null as string | null,
            vibIdCardPassportNumber: null as string | null,
            vibInvestmentFinancialAssetsOver10000: false,
            vibInvestmentIncome: false,
            vibInvestmentInformation: false,
            vibInvestmentLimit: false,
            vibInvestmentUnder1000: false,
            vibLastName: null as string | null,
            vibPlaceOfBirth: null as string | null,
            vibPostalCode: null as string | null,
            vibState: null as string | null,
            vibTaxNumber: null as string | null,
            voucherAmount: null,
            voucherifyVoucherUid: null,
            walletAmount: 0,
        }
    },
    actions: {
        async saveBasket(stage: number) {
            const basket = {
                // Temporary fix for legal profiles as it is not handled in baskets
                natural_profile_uid: usePersonalAccountDataState().naturalProfile.natural_profile_uid,
                // natural_profile_uid: isNaturalProfile()
                //     ? usePersonalAccountDataState().naturalProfile.natural_profile_uid
                //     : useLegalAccountDataState().legalProfile.legal_profile_uid,
                asset_var_uid: campaignData.asset_data.asset_var_uid,
                stage: `step-${stage}`,
            }
            const [data, error] = await promiseHandler(postBasket(basket))
            if (data) {
                return true
            }
            if (error) {
                return false
            }

            return true
        },
        async createOrder() {
            const utmParams = useUtmParams()
            const createOrderPayload = {
                amount: this.investmentAmount,
                total_amount: this.investmentAmount,
                asset_uid: campaignData.asset_data.asset_uid,
                asset_var_uid: campaignData.asset_data.asset_var_uid,
                campaignId: campaignData.asset_data.asset_var_uid,
                account_uid: isNaturalProfile()
                    ? usePersonalAccountDataState().accountId
                    : useLegalAccountDataState().accountId,
                additional_fields: [],
                accepted_risks: this.acceptedRisks,
                savings_plan: this.savingsPlan,
                is_gift: this.giftMembership,
                investor_consent: this.acceptPublicInvestmentConsent,
            }
            if (this.walletAmount) {
                createOrderPayload['wallet_amount'] = this.walletAmount
            }
            if (this.voucherAmount && this.voucherifyVoucherUid) {
                createOrderPayload['voucher_amount'] = this.voucherAmount
                createOrderPayload['voucherify_voucher_uid'] = this.voucherifyVoucherUid
            }
            if (this.contractType) {
                createOrderPayload['contract_type'] = this.contractType
            }
            if (this.appliedCoupons.length > 0) {
                createOrderPayload['applied_coupons'] = this.appliedCoupons
            }
            if (this.nyalaPassphrase) {
                createOrderPayload['nyala_passphrase'] = this.nyalaPassphrase
            }
            if (this.brokerNumber) {
                createOrderPayload['broker_number'] = this.brokerNumber
            }
            if (this.brokerDisclaimer) {
                createOrderPayload['broker_disclaimer'] = this.brokerDisclaimer
            }
            if (campaignData.asset_data.regulatory_requirements === CampaignRegulatoryRequirement.ALTFG) {
                createOrderPayload['regulatory_requirements'] = {
                    regulatory_requirement: CampaignRegulatoryRequirement.ALTFG,
                    altfg_professional_investor: this.altfgProfessionalInvestor,
                    altfg_legal_entity: this.altfgLegalEntity,
                    altfg_maximum_net_income: this.altfgMaximumNetIncome,
                    altfg_maximum_assets: this.altfgMaximumAssets,
                }
            }
            if (campaignData.asset_data.regulatory_requirements === CampaignRegulatoryRequirement.VERMANLG) {
                createOrderPayload['regulatory_requirements'] = {
                    regulatory_requirement: CampaignRegulatoryRequirement.VERMANLG,
                    city: this.vibCurrentLocation,
                    date: this.vibDate,
                    first_name_confirm: this.vibFirstName,
                    last_name_confirm: this.vibLastName,
                    place_of_birth_confirm: this.vibPlaceOfBirth,
                    date_of_birth_confirm: this.vibDateOfBirth,
                    identity_card_passport_number_confirm: this.vibIdCardPassportNumber,
                    issuing_authority_confirm: this.issuingAuthority,
                    postal_code_confirm: this.vibPostalCode,
                    state_confirm: this.vibState,
                    city_confirm: this.vibCity,
                    address_confirm: this.vibAddress,
                    email_confirm: this.vibEmail,
                    tax_number: this.vibTaxNumber,
                    accept_investment_information: this.vibInvestmentInformation,
                    accept_investment_limit: this.vibInvestmentLimit,
                    accept_investment_income: this.vibInvestmentIncome,
                    accept_investment_financial_assets_over_10000: this.vibInvestmentFinancialAssetsOver10000,
                    accept_investment_under_1000: this.vibInvestmentUnder1000,
                }
            }
            if (campaignData.asset_data.regulatory_requirements === CampaignRegulatoryRequirement.STOCK_EMISSION) {
                createOrderPayload['contract_type'] = 'stock_emission'
            }
            if (Object.keys(utmParams).length > 0) {
                Object.entries(utmParams).forEach(([key, value]) => {
                    createOrderPayload[key] = value
                })
            }
            const [data, error] = await promiseHandler(postOrder(createOrderPayload))
            if (data) {
                this.setCreatedOrder(data.data.order)
                this.deletePendingInvestment()
                return true
            }
            if (error) {
                console.error('Error creating order', error)
                return false
            }
        },
        setCreatedOrder(order: OrderResponseObject) {
            this.createdOrder = order
        },
        deletePendingInvestment() {
            deletePendingInvestmentCookie()
        },
        setPendingInvestment() {
            // do not set pending investment if new order was already created
            if (this.createdOrder) {
                return
            }
            setPendingInvestmentCookie({
                natural_profile_uid: usePersonalAccountDataState().naturalProfile.natural_profile_uid,
                account_type: isLegalProfile() ? 'legal' : 'natural',
                asset_uid: campaignData.asset_data.asset_uid,
                asset_title: campaignData.asset_data.asset_title,
                return_url: window.location.href,
                investment_amount: this.investmentAmount,
                pieces: this.pieces,
                contract_type: this.contractType,
                incentive_uid: this.incentiveUid,
                wallet_amount: this.walletAmount,
                voucher_amount: this.voucherAmount,
                voucherify_voucher_uid: this.voucherifyVoucherUid,
                applied_coupons: JSON.stringify(this.appliedCoupons),
                step: useInvestmentProcessNavigation().currentStepIndex,
            })
        },
        loadPendingInvestment() {
            const pendingInvestment = getPendingInvestmentCookie()
            if (pendingInvestment && pendingInvestment.return_url === window.location.href) {
                this.investmentAmount = pendingInvestment.investment_amount
                this.pieces = pendingInvestment.pieces
                this.contractType = pendingInvestment.contract_type
                this.incentiveUid = pendingInvestment.incentive_uid
                // this.walletAmount = pendingInvestment.wallet_amount // hotfix: disable wallet amount
                this.voucherAmount = pendingInvestment.voucher_amount
                this.voucherifyVoucherUid = pendingInvestment.voucherify_voucher_uid
                this.appliedCoupons = pendingInvestment.applied_coupons
                    ? JSON.parse(pendingInvestment.applied_coupons)
                    : []
            }
        },
    },
    getters: {
        amountToPay() {
            const couponAmount = this.appliedCoupons.reduce((sum, coupon) => sum + coupon.discount_amount, 0)
            return (
                this.investmentAmount + this.agioDisagioAmount - this.walletAmount - this.voucherAmount - couponAmount
            )
        },
        allRisksAccepted() {
            return [...campaignData.disclaimers].every((disclaimer) => this.acceptedRisks.includes(disclaimer))
        },
        incentive() {
            return campaignData.assets_incentives.find((incentive) => incentive.asset_inc_uid === this.incentiveUid)
        },
    },
})
